<script setup lang="ts">
import { BaseCard } from '@/components/Cards'
import type { DetectionModel } from '@/types/models/DetectionModel'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { AlertSocketMessageModel } from '@/types/socket/SocketAlertModel'
import { computed, inject } from 'vue'
import store from '../../../store'
import { EWebSocketPerm } from '../../../types/permissions/WebSocketPermissionsEnum'
import DeviceCardContent from './DeviceCardContent.vue'
import DeviceCardFooter from './DeviceCardFooter.vue'
import DeviceCardHeader from './DeviceCardHeader.vue'

const hideDetections = inject('hideDetectionsData', undefined)
const permissions = computed(() => store.state.authentication?.me?.permissions ?? [])
const userHasDetectionsPermission = computed(
    () => permissions.value?.find(item => item === EWebSocketPerm.WEBSOCKET_ACCESS)
)

type Props = {
    data: DeviceModel
    status?: AlertSocketMessageModel
    detections: DetectionModel[]
    showNextEvent?: boolean
    showNextCheckpoint?: boolean
}

defineProps<Props>()
type Emits = {
    (e: 'click'): void
}
defineEmits<Emits>()
</script>

<template>
    <BaseCard
        class="flex primary-border color-text pointer device-card"
        @click="$emit('click')"
        v-bind="$attrs">
        <template #title>
            <DeviceCardHeader
                :title="data.name ?? data.serial_number"
                :subtitle="data.device_type_name"
                :data="data"
                :status="status"
                class="device-card-header align-items-center" />
        </template>
        <template #content>
            <DeviceCardContent
                :data="data"
                :status="status"
                class="device-card-content"
                :showNextEvent="showNextEvent"
                :showNextCheckpoint="showNextCheckpoint">
                <slot name="content" />
            </DeviceCardContent>
        </template>
        <template v-if="!hideDetections && userHasDetectionsPermission" #footer>
            <DeviceCardFooter :latestDetections="detections" />
        </template>
    </BaseCard>
</template>

<style scoped lang="scss">
.device-card {
    flex-direction: column;
    min-height: 240px;
    height: fit-content;

    & :deep(.card-header-title) {
        min-height: 70px;
        & .title {
            font-size: large;
            color: var(--color-text);
        }
    }

    & :deep(.card-header-title .subtitle) {
        color: var(--color-text-secondary);
    }

    & :deep(.card-content) {
        min-height: 110px;
        height: 100%;
    }

    & :deep(.card-footer) {
        min-height: 150px;
        flex: auto;
    }

    & :deep(.card-header-title),
    & :deep(.card-content),
    & :deep(.card-footer) {
        padding: 0.5em;
    }
}
</style>
